import React, { useState, useContext } from 'react';
import './Profile.css';
import PasswordChangeComponent from '../common/PasswordChange';
import { UserContext } from '../../context/UserContext';
import { AuthContext } from '../../context/AuthContext';
import { User } from '../common/DataTypes';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';

const ProfileComponent: React.FC = () => {
    const userContext = useContext(UserContext);
    const { t } = useTranslation();

    if (!userContext) {
        throw new Error('ProfileComponent must be used within a UserProvider');
    }

    const { currentUser, updateUser } = userContext;
    const authContext = useContext(AuthContext);
    const token = authContext?.token || '';

    const [user, setUser] = useState<User>({ ...currentUser });
    const [originalUser, setOriginalUser] = useState<User>({ ...currentUser });
    const [isChanged, setIsChanged] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isPictureEditing, setIsPictureEditing] = useState(false);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                if (reader.result) {
                    setUser((prevUser) => {
                        const updatedUser = { ...prevUser, image: reader.result as string };
                        setIsChanged(true); // Enable save button
                        return updatedUser;
                    });
                    setIsPictureEditing(false); // Exit picture editing mode after selecting image
                }
            };
            reader.readAsDataURL(file); // Convert image to data URL
        }
    };

    const handlePictureEditClick = () => {
        setIsPictureEditing(!isPictureEditing);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUser((prevUser) => {
            const updatedUser = { ...prevUser, [name]: value };
            setIsChanged(JSON.stringify(updatedUser) !== JSON.stringify(originalUser));
            return updatedUser;
        });
    };

    const handleSave = async () => {
        const result = await updateUser(user);
        if (result.success) {
            toast.success(result.message); // Show success toast
            setOriginalUser({ ...user });
            setIsChanged(false);
            setError(null);
            setIsPictureEditing(false); // Exit picture editing mode
        } else {
            setError(result.message);
            toast.error(result.message); // Show error toast
        }
    };

    const handleCancel = () => {
        setUser({ ...originalUser }); // Restore original values
        setIsChanged(false); // Reset change tracking
        setError(null); // Clear any previous errors
        setIsPictureEditing(false); // Exit picture editing mode
    };

    const handleSendTestMail = async () => {
        try {
            const res = await fetch('/api/mails/sendTestMail', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const text = await res.text();
            if (res.ok) {
                toast.success(text || 'Test Mail erfolgreich gesendet');
            } else {
                toast.error(text || 'Fehler beim Senden der Test Mail');
            }
        } catch (e:any) {
            toast.error(e.message || 'Unerwarteter Fehler beim Senden der Test Mail');
        }
    };

    return (
        <>
            <div className="profile-card">
                <h2>{t('profile.header')}</h2>
                {error && <p style={{ color: 'red' }}>{error}</p>}
                <div className="profile-picture-container">
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img
                            src={user.image}
                            alt={t('profile.picture_alt')}
                            className="profile-picture-editable"
                        />
                        <span 
                            className="edit-icon" 
                            onClick={handlePictureEditClick}
                            style={{ cursor: 'pointer' }}
                        >
                            ✏️
                        </span>
                    </div>
                    {isPictureEditing && (
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', justifyContent: 'center' }}>
                            <input
                                id="profile-picture-input"
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleImageChange}
                            />
                            <input
                                id="profile-camera-input"
                                type="file"
                                accept="image/*"
                                capture="environment"
                                style={{ display: 'none' }}
                                onChange={handleImageChange}
                            />
                            <label htmlFor="profile-picture-input" style={{
                                padding: '0.5rem 1rem',
                                border: '1px solid #ccc',
                                borderRadius: '0.5rem',
                                cursor: 'pointer',
                                background: '#f5f5f5',
                                fontSize: '0.85rem',
                                textAlign: 'center'
                            }}>
                                📁 {t('photo_upload.choose_file')}
                            </label>
                            <label htmlFor="profile-camera-input" style={{
                                padding: '0.5rem 1rem',
                                border: '1px solid #ccc',
                                borderRadius: '0.5rem',
                                cursor: 'pointer',
                                background: '#f5f5f5',
                                fontSize: '0.85rem',
                                textAlign: 'center'
                            }}>
                                📷 {t('photo_upload.take_photo')}
                            </label>
                        </div>
                    )}
                </div>
                <div className="profile-inputs">
                    <label>{t('profile.email')}:</label>
                    <input
                        type="email"
                        name="email"
                        value={user.email}
                        onChange={handleInputChange}
                    />
                    <label>{t('profile.name')}:</label>
                    <input
                        type="text"
                        name="name"
                        value={user.name}
                        onChange={handleInputChange}
                    />
                </div>
                <div className="profile-actions">
                    <button
                        className="save-button"
                        onClick={handleSave}
                        disabled={!isChanged}
                    >
                        {t('profile.save')}
                    </button>
                    <button
                        className="cancel-button"
                        onClick={handleCancel}
                        disabled={!isChanged}
                    >
                        {t('profile.cancel')}
                    </button>
                </div>
            </div>
            <div className="profile-card">
                <PasswordChangeComponent />
            </div>
            <div className="profile-card">
                <button className="save-button" onClick={handleSendTestMail} type="button">
                    Send Test Mail
                </button>
            </div>
        </>
    );
};

export default ProfileComponent;